import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { Input } from '@/components/ui/input.jsx';
import { FileSignature, ExternalLink, X, BellRing, Download, Send, FileBadge, ClipboardEdit, CopyPlus } from 'lucide-react';
import BrRequestDialog from './BrRequestDialog.jsx';
import BrDataSheet from './BrDataSheet.jsx';
import { remindBr, markBrCarrierSubmitted, cloneBrDeal } from '@/services/deals.service.js';
import { formatDateTime, toDate } from '@/lib/date-display.js';
import { toast } from '@/components/ui/toaster.jsx';

// BrStatusPanel - broker-side trigger + status pill for Builders Risk
// binding, per BR-AND-SNKL-TOWER-SPEC.md §2.6. Mirrors SnklStatusPanel.
//
// Three pill/button states driven by item.BR_Status (graceful default
// 'not_requested' when the field is missing - the BR_* CRM fields haven't
// been added to the Deals layout yet, so the panel must work without them):
//
//   not_requested  grey  "BR not sent"                            "Request BR binding signature"
//   requested      amber "BR pending, sent {ts}"                  "Re-request" / "Cancel"
//   bound          green "BR bound by {Signer} on {ts}"           "View signed PDF" / "Re-request"
//
// Re-request after bound does NOT clear the prior BR; it mints a fresh
// token and overwrites the headline status fields. Full history lives in
// policy_transactions queried by crm_deal_id + type='bind'.
//
// No em dashes anywhere (CLAUDE.md hard rule).

const shortDateTime = (iso) => formatDateTime(iso);

function firstNameOf(full) {
  if (!full) return '';
  return String(full).trim().split(/\s+/)[0] || '';
}

// Compact relative time: "3h ago", "2d ago", "just now". Used on the
// pending pill so the broker sees age at a glance and can decide
// whether to remind.
function relativeShort(iso) {
  if (!iso) return '';
  const t = toDate(iso)?.getTime() ?? NaN;
  if (Number.isNaN(t)) return '';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

export default function BrStatusPanel({ item, dealId, onChange, onIssueCoi }) {
  // Graceful default: if the BR_* fields aren't on the deal payload
  // (because David hasn't added them to Zoho yet), treat as not_requested.
  const status = item?.BR_Status || 'not_requested';
  const signerName = item?.BR_Signer_Name || '';
  const boundAt = item?.BR_Bound_At || '';
  const requestedAt = item?.BR_Requested_At || '';
  const pdfUrl = item?.BR_Bind_PDF_URL || '';
  const packageUrl = item?.BR_Package_URL || '';
  const carrierSubmittedAt = item?.BR_Carrier_Submitted_At || '';
  const carrierSubmittedTo = item?.BR_Carrier_Submitted_To || '';

  const lastRemindedAt = item?.BR_Last_Reminded_At || '';
  // Dialog launches BrRequestDialog which owns the send flow + the
  // post-send link display. The panel itself is just status + launcher.
  const [dialogOpen, setDialogOpen] = useState(false);
  const openDialog = () => { if (dealId) setDialogOpen(true); };

  // Ship 2b: BrDataSheet is the project + terms editor. Launched here (and
  // reachable pre-send from the binding action's dialog surface).
  const [dataSheetOpen, setDataSheetOpen] = useState(false);

  // Ship 2c: "Clone for next building" - fresh Deal for the same insured +
  // carrier, blob copied, all signing/payment state blank server-side.
  const navigate = useNavigate();
  const accountIdForNav = item?.Account_Name?.id || item?.account_id || item?.crm_account_id || null;
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [cloning, setCloning] = useState(false);
  const [cloneErr, setCloneErr] = useState('');
  async function confirmClone() {
    const name = cloneName.trim();
    if (!dealId || cloning || !name) return;
    setCloning(true); setCloneErr('');
    try {
      const res = await cloneBrDeal(dealId, { dealName: name });
      if (!res?.success || !res?.deal_id) {
        setCloneErr(res?.error || 'Clone failed');
      } else {
        setCloneOpen(false); setCloneName('');
        onChange?.();
        if (accountIdForNav) navigate(`/account/${accountIdForNav}/deal/${res.deal_id}`);
      }
    } catch (e) {
      setCloneErr(e?.body?.error || e?.message || 'Clone failed');
    }
    setCloning(false);
  }

  // Remind state: re-send the SAME signing link (no new token). Distinct
  // from Re-request which mints a fresh token via the dialog. The panel
  // updates the pill optimistically by writing the new BR_Last_Reminded_At
  // back through onChange so the broker sees immediate feedback rather
  // than waiting for the 15min pipeline cache to refresh.
  const [reminding, setReminding] = useState(false);
  const [remindErr, setRemindErr] = useState('');
  async function sendRemind() {
    if (!dealId || reminding) return;
    setReminding(true); setRemindErr('');
    try {
      const res = await remindBr(dealId);
      if (!res?.success) setRemindErr(res?.error || 'Reminder failed');
      else { for (const w of res.warnings || []) toast.info(w); onChange?.(); }
    } catch (e) {
      setRemindErr(e?.body?.error || e?.message || 'Reminder failed');
    }
    setReminding(false);
  }

  // Mark submitted (Ship 1b): stamps BR_Carrier_Submitted_At / _To after the
  // broker forwards the binding package from Gmail. Click reveals a small
  // confirm row; the email input is optional (blank = the server resolves
  // the carriers-table default underwriter contact at submit time, carrier
  // emails never live in the client either).
  const [markOpen, setMarkOpen] = useState(false);
  const [markEmail, setMarkEmail] = useState('');
  const [marking, setMarking] = useState(false);
  const [markErr, setMarkErr] = useState('');
  async function confirmMarkSubmitted() {
    if (!dealId || marking) return;
    setMarking(true); setMarkErr('');
    try {
      const res = await markBrCarrierSubmitted(dealId, { submittedTo: markEmail.trim() });
      if (!res?.success) setMarkErr(res?.error || 'Mark submitted failed');
      else { setMarkOpen(false); onChange?.(); }
    } catch (e) {
      setMarkErr(e?.body?.error || e?.message || 'Mark submitted failed');
    }
    setMarking(false);
  }

  let pill;
  // Carrier-submission pill (bound state only): amber until the broker marks
  // the package forwarded, then green with recipient + timestamp. The amber
  // pill is the anti-PlaneWay device: a signed app once sat three weeks
  // unforwarded because nothing surfaced the gap.
  let submittedPill = null;
  if (status === 'bound') {
    const label = signerName
      ? `BR bound by ${signerName}${boundAt ? ` on ${shortDateTime(boundAt)}` : ''}`
      : 'BR bound';
    pill = (
      <Badge variant="success" className="gap-1 text-[10px]" title={signerName ? `${signerName} - ${boundAt}` : boundAt}>
        <FileSignature size={11} strokeWidth={2} />
        {label}
      </Badge>
    );
    submittedPill = carrierSubmittedAt ? (
      <Badge variant="success" className="gap-1 text-[10px]" title={`Submitted ${carrierSubmittedAt}`}>
        <Send size={11} strokeWidth={2} />
        {`Submitted to ${carrierSubmittedTo || 'carrier'} ${shortDateTime(carrierSubmittedAt)}`}
      </Badge>
    ) : (
      <Badge variant="warning" className="gap-1 text-[10px]" title="Forward the binding package to the carrier underwriter, then mark submitted.">
        <Send size={11} strokeWidth={2} />
        Not yet submitted to carrier
      </Badge>
    );
  } else if (status === 'requested') {
    // Pill text prefers the most-recent reminder timestamp so the
    // broker sees "last reminded 2h ago" not the stale original send
    // when a reminder has been fired. Falls back to original request
    // time when no reminder yet.
    const showAt = lastRemindedAt || requestedAt;
    const verb = lastRemindedAt ? 'reminded' : 'sent';
    pill = (
      <Badge variant="warning" className="gap-1 text-[10px]" title={`Requested ${requestedAt}${lastRemindedAt ? ` · last reminded ${lastRemindedAt}` : ''}`}>
        <FileSignature size={11} strokeWidth={2} />
        {showAt ? `BR pending, ${verb} ${relativeShort(showAt)}` : 'BR pending'}
      </Badge>
    );
  } else {
    pill = (
      <Badge variant="outline" className="gap-1 text-[10px] text-text-muted">
        <FileSignature size={11} strokeWidth={2} />
        BR not sent
      </Badge>
    );
  }

  const primaryBtn = (text) => (
    <Button
      size="sm"
      variant="outline"
      className="w-full justify-start gap-2 border-border-strong hover:border-accent hover:bg-accent-soft hover:text-accent"
      onClick={openDialog}
    >
      <FileSignature size={14} strokeWidth={1.5} />
      <span className="text-xs">{text}</span>
    </Button>
  );

  const secondaryBtns = (
    <>
      {packageUrl && status === 'bound' ? (
        <Button
          asChild
          size="sm"
          variant="outline"
          className="w-full justify-start gap-2 border-border-strong hover:border-accent hover:bg-accent-soft hover:text-accent"
        >
          <a href={packageUrl} target="_blank" rel="noopener noreferrer">
            <Download size={14} strokeWidth={1.5} />
            <span className="text-xs">Download binding package</span>
          </a>
        </Button>
      ) : null}
      {status === 'bound' && !carrierSubmittedAt ? (
        markOpen ? (
          <div className="flex flex-col gap-1.5 rounded-card border border-border-strong p-2">
            <Input
              value={markEmail}
              onChange={(e) => setMarkEmail(e.target.value)}
              placeholder="Underwriter email (blank = carriers table default)"
              className="h-7 text-xs"
            />
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={confirmMarkSubmitted} disabled={marking}>
                {marking ? 'Marking...' : 'Confirm submitted'}
              </Button>
              <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setMarkOpen(false); setMarkErr(''); }}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="w-full justify-start gap-2 border-border-strong hover:border-accent hover:bg-accent-soft hover:text-accent"
            onClick={() => setMarkOpen(true)}
            title="Record that the binding package was forwarded to the carrier underwriter."
          >
            <Send size={14} strokeWidth={1.5} />
            <span className="text-xs">Mark submitted</span>
          </Button>
        )
      ) : null}
      {status === 'bound' && typeof onIssueCoi === 'function' ? (
        <Button
          size="sm"
          variant="ghost"
          className="w-full justify-start gap-2 text-text-muted hover:text-text"
          onClick={onIssueCoi}
          title="Open the ACORD 28 certificate flow. This insured requests one every building."
        >
          <FileBadge size={14} strokeWidth={1.5} />
          <span className="text-xs">Issue COI</span>
        </Button>
      ) : null}
      {pdfUrl && status === 'bound' ? (
        <Button asChild size="sm" variant="ghost" className="w-full justify-start gap-2 text-text-muted hover:text-text">
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink size={14} strokeWidth={1.5} />
            <span className="text-xs">View signed PDF</span>
          </a>
        </Button>
      ) : null}
      {status === 'requested' ? (
        <Button
          size="sm"
          variant="outline"
          className="w-full justify-start gap-2 border-border-strong hover:border-accent hover:bg-accent-soft hover:text-accent"
          onClick={sendRemind}
          disabled={reminding}
          title="Re-send the existing signing link. Use Re-request only when the prior link expired or you need to invalidate it."
        >
          <BellRing size={14} strokeWidth={1.5} />
          <span className="text-xs">{reminding ? 'Sending reminder...' : 'Remind'}</span>
        </Button>
      ) : null}
      {status === 'requested' ? (
        <Button
          size="sm"
          variant="ghost"
          className="w-full justify-start gap-2 text-text-muted hover:text-text"
          onClick={() => { /* TODO: backend cancel endpoint - keep visible but no-op for v1 */ }}
          disabled
          title="Cancel is not wired up in v1. Re-request instead to mint a fresh token."
        >
          <X size={14} strokeWidth={1.5} />
          <span className="text-xs">Cancel</span>
        </Button>
      ) : null}
    </>
  );

  return (
    <>
      <div className="rounded-card border border-border bg-surface p-3 shadow-sm">
        <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">
          Builders Risk Binding
        </h3>
        <div className="mb-2 flex flex-wrap gap-1.5">{pill}{submittedPill}</div>
        <div className="flex flex-col gap-1.5">
          {status === 'not_requested' ? primaryBtn('Request BR binding signature') : null}
          {/* When already requested, Remind (re-send same link) is the
              primary action; Re-request (new token) demotes to ghost. */}
          {status === 'requested' ? null : (status === 'bound' ? primaryBtn('Re-request') : null)}
          {secondaryBtns}
          {status === 'requested' ? (
            <Button
              size="sm"
              variant="ghost"
              className="w-full justify-start gap-2 text-text-muted hover:text-text"
              onClick={openDialog}
              title="Mints a NEW signing token (the prior link stops working). Use Remind if you just want to re-send the existing link."
            >
              <FileSignature size={14} strokeWidth={1.5} />
              <span className="text-xs">Re-request (new link)</span>
            </Button>
          ) : null}
          {/* Ship 2: project entry + repeat-building doors. Always visible;
              editing never touches signed artifacts and clone is how the
              next building starts. */}
          <Button
            size="sm"
            variant="outline"
            className="w-full justify-start gap-2 border-border-strong hover:border-accent hover:bg-accent-soft hover:text-accent"
            onClick={() => { if (dealId) setDataSheetOpen(true); }}
            title="Enter or correct the project, coverage, premium, and terms. The readiness strip inside shows what the signature send still needs."
          >
            <ClipboardEdit size={14} strokeWidth={1.5} />
            <span className="text-xs">Edit project &amp; terms</span>
          </Button>
          {cloneOpen ? (
            <div className="flex flex-col gap-1.5 rounded-card border border-border-strong p-2">
              <Input
                value={cloneName}
                onChange={(e) => setCloneName(e.target.value)}
                placeholder='New deal name, e.g. "PUJ Hangar 680"'
                className="h-7 text-xs"
              />
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={confirmClone} disabled={cloning || !cloneName.trim()}>
                  {cloning ? 'Cloning...' : 'Create deal'}
                </Button>
                <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setCloneOpen(false); setCloneErr(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="w-full justify-start gap-2 text-text-muted hover:text-text"
              onClick={() => setCloneOpen(true)}
              title="Fresh deal for the next building: same insured, carrier, and project data; policy number, signatures, and payment all start blank."
            >
              <CopyPlus size={14} strokeWidth={1.5} />
              <span className="text-xs">Clone for next building</span>
            </Button>
          )}
        </div>
        {remindErr ? <p className="mt-2 text-[10px] text-danger">{remindErr}</p> : null}
        {markErr ? <p className="mt-2 text-[10px] text-danger">{markErr}</p> : null}
        {cloneErr ? <p className="mt-2 text-[10px] text-danger">{cloneErr}</p> : null}
      </div>
      <BrRequestDialog
        dealId={dealId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onChange={onChange}
      />
      <BrDataSheet
        dealId={dealId}
        open={dataSheetOpen}
        onOpenChange={setDataSheetOpen}
        onSaved={onChange}
      />
    </>
  );
}
